import { Pipe, PipeTransform } from '@angular/core';

/* eslint-disable unicorn/prefer-string-replace-all */
@Pipe({
  name: 'markdownRendering',
})
export class MarkdownRenderingPipe implements PipeTransform {
  private static readonly MAX_INDENTATION_LENGTH = 64;
  private static readonly MAX_HEADING_GAP_LENGTH = 256;
  private static readonly MAX_HEADING_TEXT_LENGTH = 16384;
  private static readonly MAX_TAG_CONTENT_LENGTH = 4096;
  private static readonly MAX_INLINE_WHITESPACE_LENGTH = 256;
  private static readonly MAX_TRAILING_WHITESPACE_LENGTH = 1024;
  private static readonly MAX_HORIZONTAL_RULE_WHITESPACE_LENGTH = 1024;

  // Captures markdown heading lines (with optional blockquote/indentation) to rewrap them as HTML headings.
  // Quantifiers are explicitly bounded to prevent super-linear backtracking flagged by static analysis tools.
  private readonly headingRegex = new RegExp(
    String.raw`(^|>)([^\S\r\n]{0,${MarkdownRenderingPipe.MAX_INDENTATION_LENGTH}})(#{1,6})[^\S\r\n]{1,${MarkdownRenderingPipe.MAX_HEADING_GAP_LENGTH}}([^\r\n]{1,${MarkdownRenderingPipe.MAX_HEADING_TEXT_LENGTH}})`,
    'gm',
  );
  // Detects sequences like "<br><br>##" and inserts a newline before the hashes so heading detection works.
  private readonly tagRegex = new RegExp(
    String.raw`<[^>]{1,${MarkdownRenderingPipe.MAX_TAG_CONTENT_LENGTH}}>`,
    'g',
  );
  // Finds HTML tags (possibly spanning lines) so that embedded newlines can be stripped out before heading parsing.
  private readonly whitespaceAroundNewlineRegex = new RegExp(
    String.raw`[^\S\r\n]{0,${MarkdownRenderingPipe.MAX_INLINE_WHITESPACE_LENGTH}}\r?\n[^\S\r\n]{0,${MarkdownRenderingPipe.MAX_INLINE_WHITESPACE_LENGTH}}`,
    'g',
  );
  // Captures trailing whitespace before a break so it can be reattached outside of the heading element.
  private readonly trailingWhitespaceRegex = new RegExp(
    String.raw`[^\S\r\n]{1,${MarkdownRenderingPipe.MAX_TRAILING_WHITESPACE_LENGTH}}$`,
  );
  // Detects markdown horizontal rules (---, ***, ___) with optional whitespace and converts them to spacing only.
  private readonly horizontalRuleRegex = new RegExp(
    String.raw`(^|[\r\n]|<br[^\S\r\n]{0,${MarkdownRenderingPipe.MAX_INLINE_WHITESPACE_LENGTH}}\/?>)([^\S\r\n]{0,${MarkdownRenderingPipe.MAX_HORIZONTAL_RULE_WHITESPACE_LENGTH}})(?:-{3,}|\*{3,}|_{3,})([^\S\r\n]{0,${MarkdownRenderingPipe.MAX_HORIZONTAL_RULE_WHITESPACE_LENGTH}})(?=(?:<br[^\S\r\n]{0,${MarkdownRenderingPipe.MAX_INLINE_WHITESPACE_LENGTH}}\/?>|[\r\n]|$))`,
    'gim',
  );

  transform(value?: string | null): string {
    if (!value) {
      return value ?? '';
    }

    let output = this.normalizeHashEntities(value);
    output = this.transformHorizontalRules(output);
    output = this.transformHeadings(output);

    return output;
  }

  private normalizeHashEntities(value: string): string {
    // Matches numeric or named HTML entities for '#' so they can be normalized to the literal character.
    return value.replace(/(&#35;|&#x23;|&num;)/gi, '#');
  }

  private transformHeadings(value: string): string {
    let working = value.replace(this.tagRegex, (tag: string) => tag.replace(this.whitespaceAroundNewlineRegex, ' '));
    working = working.replace(
      /((?:<br\s*\/?>\s*)+)(#{1,6})/gi,
      (_match, breaks: string, hashes: string) => `${breaks}\n${hashes}`,
    );

    working = working.replace(
      this.headingRegex,
      (match: string, prefix: string, indentation: string, hashes: string, restOfLine: string) => {
        const headingLevel = hashes.length;
        const adjustedLevel = Math.min(headingLevel + 2, 6);
        const leadingWhitespace = this.getLeadingWhitespace(restOfLine);
        const content = restOfLine.slice(leadingWhitespace.length);

        if (!content.trim()) {
          return match;
        }

        // Finds the first HTML break or newline so the heading text can be isolated.
        const breakIndex = content.search(/(<br\s*\/?>|\r|\n)/i);
        let headingText: string;
        let remainder: string;

        if (breakIndex === -1) {
          headingText = content.trimEnd();
          remainder = '';
        } else {
          const beforeBreak = content.slice(0, breakIndex);
          const trailingWhitespaceMatch = this.trailingWhitespaceRegex.exec(beforeBreak);
          const trailingWhitespace = trailingWhitespaceMatch ? trailingWhitespaceMatch[0] : '';
          headingText = beforeBreak.slice(0, beforeBreak.length - trailingWhitespace.length).trimEnd();
          remainder = `${trailingWhitespace}${content.slice(breakIndex)}`;
        }

        if (!headingText) {
          return match;
        }

        return `${prefix}${indentation}${leadingWhitespace}<h${adjustedLevel}>${headingText}</h${adjustedLevel}>${remainder}`;
      },
    );

    // Removes stray newline characters between <br> tags and newly inserted <h#> tags.
    return working.replace(/((?:<br\s*\/?>\s*))\n(?=<h[1-6]>)/gi, '$1');
  }

  private transformHorizontalRules(value: string): string {
    return value.replace(
      this.horizontalRuleRegex,
      (_match, prefix: string, leadingWhitespace: string, trailingWhitespace: string) =>
        `${prefix}${leadingWhitespace}${trailingWhitespace}`,
    );
  }

  private getLeadingWhitespace(value: string): string {
    let index = 0;
    const isNonNewlineWhitespace = (char: string) => !!char && char !== '\r' && char !== '\n' && char.trim() === '';

    while (index < value.length && isNonNewlineWhitespace(value[index])) {
      index += 1;
    }
    return value.slice(0, index);
  }
}

